/**
 * Flat and per-drop layouts for AI country results.
 */

import { Layers } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { DropLabel } from '@/features/campaign-texts/components/PerDropGroupSelector';
import type { TreeGroupLike } from '@/features/campaign-texts/utils/group-tree.utils';

import type { CountryGenResult } from '../../shared/multiCountryBulkEdit.types';
import { buildPerDropRows, groupRowsByPrompt } from '../../shared/dropResultLayout';

import { resolvePromptGroupLabel } from '../resolvePromptGroupLabel';

import type { AiTextPickInteraction } from './aiTextPickInteraction.types';
import { AiResultTextBatch } from './AiResultTextBatch';
import { DropSection, SenderChips } from './countryResultDropSection';

const liCls = 'truncate text-xs text-muted-foreground';

export function FlatContent({
  result,
  textPickInteraction,
}: {
  result: CountryGenResult;
  textPickInteraction?: AiTextPickInteraction;
}) {
  const hasTexts = result.texts.length > 0;
  const usePicks = Boolean(textPickInteraction && hasTexts);
  return (
    <>
      {hasTexts &&
        (usePicks ? (
          <AiResultTextBatch
            texts={result.texts}
            translations={result.translations}
            picksRow={textPickInteraction?.picks?.[0]}
            disableInteractions={textPickInteraction?.disableInteractions}
            onToggleLine={(li) => textPickInteraction!.toggle(0, li)}
            onSetAllInBatch={(v) => textPickInteraction!.setAllInBatch(0, v)}
            onRegenUnselected={() => textPickInteraction!.regenUnselected(0)}
            isRegenerating={textPickInteraction!.isBatchRegenerating(0)}
            showSectionTranslate={textPickInteraction!.enableSectionTranslate}
            sectionNeedsEnglish={textPickInteraction!.batchNeedsEnglishTranslation?.(0)}
            isTranslatingSection={textPickInteraction!.isBatchTranslating?.(0)}
            onTranslateSection={() => void textPickInteraction!.translateMissingInBatch?.(0)}
          />
        ) : (
          <ul className="max-h-60 divide-y divide-border/30 overflow-y-auto rounded-md border border-border/25">
            {result.texts.map((t, i) => (
              <li key={i} className="px-2 py-2">
                <div className={liCls}>{t}</div>
                {result.translations?.[i] ? (
                  <div className="mt-0.5 truncate text-[10px] text-amber-700 dark:text-amber-400">
                    {result.translations[i]}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ))}
      {result.senders.length > 0 && <SenderChips senders={result.senders} />}
    </>
  );
}

export function PerDropGroupedContent({
  result,
  dropLabels,
  promptGroups,
  textPickInteraction,
}: {
  result: CountryGenResult;
  dropLabels?: DropLabel[];
  promptGroups?: TreeGroupLike[];
  textPickInteraction?: AiTextPickInteraction;
}) {
  const sections = groupRowsByPrompt(buildPerDropRows(result, dropLabels));
  return (
    <div className="space-y-5">
      {sections.map((sec, si) => {
        const showHeader = sec.key !== '__flat__';
        const label = showHeader ? resolvePromptGroupLabel(sec.key, promptGroups) ?? sec.key : null;
        return (
          <div key={sec.key} className={cn(si > 0 && 'border-t border-border/35 pt-5')}>
            {label != null && (
              <div className="mb-3 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Layers className="h-3 w-3 shrink-0 opacity-70" />
                <span className="min-w-0 break-words normal-case text-foreground">{label}</span>
              </div>
            )}
            <div className="space-y-0">
              {sec.rows.map((r, ri) => (
                <DropSection key={r.di} title={r.title} senders={r.senders} isFirst={ri === 0}>
                  {r.texts.length > 0 ? (
                    textPickInteraction ? (
                      <AiResultTextBatch
                        texts={r.texts}
                        translations={r.translations}
                        picksRow={textPickInteraction.picks?.[r.di]}
                        disableInteractions={textPickInteraction.disableInteractions}
                        onToggleLine={(li) => textPickInteraction.toggle(r.di, li)}
                        onSetAllInBatch={(v) => textPickInteraction.setAllInBatch(r.di, v)}
                        onRegenUnselected={() => textPickInteraction.regenUnselected(r.di)}
                        isRegenerating={textPickInteraction.isBatchRegenerating(r.di)}
                        showSectionTranslate={textPickInteraction.enableSectionTranslate}
                        sectionNeedsEnglish={textPickInteraction.batchNeedsEnglishTranslation?.(r.di)}
                        isTranslatingSection={textPickInteraction.isBatchTranslating?.(r.di)}
                        onTranslateSection={() => void textPickInteraction.translateMissingInBatch?.(r.di)}
                      />
                    ) : (
                      <ul className="divide-y divide-border/30 overflow-hidden rounded-md border border-border/25">
                        {r.texts.map((t, i) => (
                          <li key={i} className="px-2 py-2">
                            <div className={liCls}>{t}</div>
                            {r.translations?.[i] ? (
                              <div className="mt-0.5 truncate text-[10px] text-amber-700 dark:text-amber-400">
                                {r.translations[i]}
                              </div>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )
                  ) : null}
                </DropSection>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
